import { Button } from '@fountain/fountain-ui-components';
import classNames from 'classnames';
import React, { ReactNode, useEffect } from 'react';

import { usePostHog } from 'hooks/usePostHog';

import { dispatchOpenCue } from 'shared/dispatch-copilot-command';
import { GradientSparklesIcon } from 'images/GradientSparklesIcon';
import type { AnalyticsEvent } from 'utils/analytics';

import { useStyles } from './styles';

export type CueCtaAnalytics<
  ViewEvent extends string = string,
  ClickEvent extends string = string,
> = {
  ctaId: string;
  viewEvent: AnalyticsEvent<ViewEvent>;
  clickEvent: AnalyticsEvent<ClickEvent>;
};

export interface CueCtaButtonProps<
  ViewEvent extends string,
  ClickEvent extends string,
> {
  children: ReactNode;
  message: string;
  analytics: CueCtaAnalytics<ViewEvent, ClickEvent>;
  fullWidth?: boolean;
  disabled?: boolean;
  className?: string;
  /**
   * Send `message` to Cue without showing it as a user bubble. For a message
   * written for the agent rather than by the recruiter, where showing it reads
   * as if they said something they did not.
   *
   * The bubble is suppressed in this tab only. The message is still persisted
   * and still surfaces in the sidebar and on reload, so `message` has to read
   * acceptably to the recruiter anyway. See `OpenCuePayload.hidden`.
   */
  hideMessage?: boolean;
}

export function CueCtaButton<
  ViewEvent extends string,
  ClickEvent extends string,
>({
  children,
  message,
  analytics,
  fullWidth = false,
  disabled = false,
  className,
  hideMessage = false,
}: CueCtaButtonProps<ViewEvent, ClickEvent>): React.ReactElement {
  const classes = useStyles();
  const posthog = usePostHog();
  const { ctaId, viewEvent, clickEvent } = analytics;

  useEffect(() => {
    posthog.capture(viewEvent, { cta_id: ctaId });
  }, [posthog, viewEvent, ctaId]);

  const handleClick = () => {
    posthog.capture(clickEvent, { cta_id: ctaId });
    dispatchOpenCue({ message, openMethod: ctaId, hidden: hideMessage });
  };

  return (
    <div
      className={classNames(classes.gradientBorder, {
        [classes.gradientBorderFullWidth]: fullWidth,
      })}
    >
      <Button
        type="secondary"
        fullWidth={fullWidth}
        disabled={disabled}
        className={classNames(classes.innerButton, className)}
        startIcon={<GradientSparklesIcon className={classes.icon} />}
        onClick={handleClick}
      >
        {children}
      </Button>
    </div>
  );
}
